import * as React from 'react'
import { ImportStep } from './types'
import { useUploadMutation, isUploadAbortError } from './api/uploadMutation'
import { useUploadStatusPooling } from '@/lib/api/company/fileProcessing/status/useUploadStatusPooling'
import { useProcessedFilesReadModel } from '@/modules/imports/hooks/useProcessedFilesReadModel'
import { ImportedFileLogsSubmitDto } from '@/modules/imports/model/imports.submit.dto'
import { ImportSource } from '../../enums'

export type ImportSummarySnapshot = {
  selectedDrivers: number
  totalDrivers: number
  selectedVehicles: number
  totalVehicles: number
  /** Registration numbers of vehicles visible after dateFrom filtering */
  filteredVehicleRegistrationNumbers: string[]
}

type Props = {
  companyId: string
  handleClose?: () => void
  onSuccess?: () => void
  uploadingType: ImportSource
}

export const countAllItems = (dto: ImportedFileLogsSubmitDto): number => {
  return (
    dto.existingDrivers.length +
    dto.ignoredDrivers.length +
    dto.newDrivers.length +
    dto.existingVehicles.length +
    dto.ignoredVehicles.length +
    dto.newVehicles.length
  )
}

export const useImportFlow = ({ uploadingType, companyId, handleClose, onSuccess }: Props) => {
  const [step, setStep] = React.useState<ImportStep>('user:uploadFiles')
  const uploadTaskId = React.useRef<string | null>(null)
  const processedFileId = React.useRef<string | null>(null)
  const importTaskId = React.useRef<string | null>(null)
  const filesCount = React.useRef<number>(0)
  // const uploadedFilesCount = React.useRef<number>(0)
  const logsCount = React.useRef<number>(0)
  const summarySnapshotRef = React.useRef<ImportSummarySnapshot | null>(null)
  const [uploadedFilesCount, setUploadedFilesCount] = React.useState<number>(0)
  const uploadAbortedRef = React.useRef(false)
  const uploadAbortControllerRef = React.useRef<AbortController | null>(null)

  // 4.fetching:uploadDetails -> 5. user:mapAndSelect
  const queryUploadDetails = useProcessedFilesReadModel({
    companyId,
    fileId: processedFileId.current,
    disabled: step !== 'fetching:uploadDetails',
  })
  React.useEffect(() => {
    if (queryUploadDetails.data) {
       setStep('user:confirmSummary')
    }
  }, [queryUploadDetails.data])

  // 3. polling:uploadStatus -> 4.fetching:uploadDetails
  useUploadStatusPooling({
    companyId,
    taskId: uploadTaskId.current,
    fileId: processedFileId.current,
    disabled: step !== 'polling:uploadStatus',
    onDone: () => setStep('fetching:uploadDetails'),
    intervalMs: 2000,
  })

  // 1.user:uploadFiles -> 2.sending:uploadFiles
  // 2.sending:uploadFiles -> 3. polling:uploadStatus
  const uploadMutation = useUploadMutation({
    // uploadAbortedRef,
    companyId,
    setUploadedFilesCount: (num) => {
      setUploadedFilesCount(num)
    },
    onSuccess: (fileId: string, taskId: string) => {
      if (uploadAbortedRef.current) return
      processedFileId.current = fileId
      uploadTaskId.current = taskId

      setStep('polling:uploadStatus')
    },
  })

  // Hide abort error in UI (user chose to cancel, not a real failure)
  React.useEffect(() => {
    if (uploadMutation.isError && uploadMutation.error && isUploadAbortError(uploadMutation.error)) {
      uploadMutation.reset()
    }
  }, [uploadMutation.isError, uploadMutation.error, uploadMutation])

  // 1.user:uploadFiles -> 2.sending:uploadFiles
  const handleUploadFiles = (files: File[]) => {
    filesCount.current = files.length
    setStep('sending:uploadFiles')
    const controller = new AbortController()
    uploadAbortControllerRef.current = controller
    uploadMutation
      .mutateAsync({ files, signal: controller.signal })
      .catch((err: unknown) => {
        if (!isUploadAbortError(err)) throw err
      })
  }

  const handleRestart = () => {
    uploadAbortedRef.current = false
    uploadAbortControllerRef.current = null
    // restart
    uploadTaskId.current = null
    processedFileId.current = null
    importTaskId.current = null
    filesCount.current = 0
    // uploadedFilesCount.current = 0
    setUploadedFilesCount(0)
    logsCount.current = 0
    summarySnapshotRef.current = null
    // restart step
    setStep('user:uploadFiles')
    // close
    if (handleClose) handleClose()
  }

  const handleAbortAndReset = () => {
    // stop any inflight upload
    uploadAbortedRef.current = true
    uploadAbortControllerRef.current?.abort()

    // stop showing pending states & errors
    uploadMutation.reset()

    // ensure we are not in import polling step anymore
    setStep('user:uploadFiles')

    // full cleanup
    handleRestart()
  }

  const handleFinish = () => {
    if (onSuccess) onSuccess()
    handleRestart()
  }

  return {
    uploadDetails: queryUploadDetails.data,
    step,
    filesCount: filesCount.current,
    uploadedFilesCount: uploadedFilesCount,
    logsCount: logsCount.current,
    summarySnapshot: summarySnapshotRef.current,
    handleUploadFiles,
    handleFinish,
    handleRestart,
    handleAbortAndReset,
  }
}
